import en from 'app/localization/en.json';
import fr from 'app/localization/fr.json';
import es from 'app/localization/es.json';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as yup from 'yup';
import { DEFAULT_LANGUAGE, getDeviceLanguage, getI18nLanguage, supportedLanguages } from 'app/lib/language';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import type { CurrencyType } from 'app/schemas/user';
import { env } from 'app/lib/env';

// Would really like to be able to do dynamic import, and thus check if those files exist, and import if they do, but in React Native, you can't do that... :(
import frPartake from 'app/localization/fr-partake.json';
import enPartake from 'app/localization/en-partake.json';
import esPartake from 'app/localization/es-partake.json';
import frRefrain from 'app/localization/fr-refrain.json';
import enRefrain from 'app/localization/en-refrain.json';
import esRefrain from 'app/localization/es-refrain.json';
import frTessier from 'app/localization/fr-tessier.json';
import enTessier from 'app/localization/en-tessier.json';
import esTessier from 'app/localization/es-tessier.json';
import frMutuo from 'app/localization/fr-mutuo.json';
import frHauteGaspesie from 'app/localization/fr-hautegaspesie.json';
import frVous from 'app/localization/fr-vous.json';

const i18n = i18next;
const deviceLanguage = getDeviceLanguage();

type LocalizationValue = string | LocalizationValue[] | { [key: string]: LocalizationValue };
type LocalizationResources = Record<string, LocalizationValue>;

// Inject the custom resources (eg. for Refrain), into base resources (fr and en)
const injectCustomResources = (originalValue: LocalizationResources, customValue: LocalizationResources) => {
  for (const key in customValue) {
    const customValue1 = customValue[key];
    if (typeof customValue1 === 'string' || Array.isArray(customValue1)) {
      originalValue[key] = customValue1;
    } else {
      if (!(key in originalValue)) {
        originalValue[key] = {};
      }
      injectCustomResources(originalValue[key] as LocalizationResources, customValue1 as LocalizationResources);
    }
  }
};

if (env.IS_PARTAKE) {
  injectCustomResources(fr, frPartake);
  injectCustomResources(en, enPartake);
  injectCustomResources(es, esPartake);
}
if (isWhiteLabel(WhiteLabel.HauteGaspesie)) {
  injectCustomResources(fr, frHauteGaspesie);
}
if (isWhiteLabel(WhiteLabel.Refrain)) {
  injectCustomResources(fr, frRefrain);
  injectCustomResources(en, enRefrain);
  injectCustomResources(es, esRefrain);
}
if (isWhiteLabel(WhiteLabel.SerresTessier)) {
  injectCustomResources(fr, frTessier);
  injectCustomResources(en, enTessier);
  injectCustomResources(es, esTessier);
}
if (isFeatureEnabled(FeatureFlag.useFrVous)) {
  injectCustomResources(fr, frVous);
}
if (isWhiteLabel(WhiteLabel.Mutuo)) {
  injectCustomResources(fr, frMutuo);
}

void i18n.use(initReactI18next).init({
  compatibilityJSON: 'v4',
  interpolation: {
    escapeValue: false,
  },
  supportedLngs: supportedLanguages,
  resources: {
    en,
    fr,
    es,
  },
  lng: deviceLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  keySeparator: '.',
});

export const formatCurrency = (
  amount: number,
  currency: CurrencyType,
  numDigits = 2,
  language: string = getI18nLanguage(),
) =>
  new Intl.NumberFormat(language.substring(0, 2), {
    style: 'currency',
    currency,
    minimumFractionDigits: numDigits,
  })
    .format(amount)
    .replace(/\$(?:CA|US)/, '$');

yup.setLocale({
  mixed: {
    default: () => i18n.t('fieldErrorDefault', { ns: 'common' }),
    required: () => i18n.t('fieldErrorRequired', { ns: 'common' }),
  },
  string: {
    email: () => i18n.t('fieldErrorPatternEmail', { ns: 'common' }),
    length: ({ length }) => i18n.t('fieldErrorLength', { ns: 'common', length }),
    max: ({ max }) => i18n.t('fieldErrorTooLong', { ns: 'common', max }),
    min: ({ min }) => i18n.t('fieldErrorTooShort', { ns: 'common', min }),
  },
});

export { i18n };
